// Small geometry helpers for "does this area touch the drawn shape". No libraries.

export type Pt = [number, number]   // [lng, lat]

const inRing = (p: number[], ring: number[][]): boolean => {
    let inside = false
    for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
        const [xi, yi] = ring[i]
        const [xj, yj] = ring[j]
        if ((yi > p[1]) !== (yj > p[1]) && p[0] < ((xj - xi) * (p[1] - yi)) / (yj - yi) + xi) {
            inside = !inside
        }
    }
    return inside
}

const orient = (p: number[], q: number[], r: number[]) =>
    (q[0] - p[0]) * (r[1] - p[1]) - (q[1] - p[1]) * (r[0] - p[0])

const segmentsCross = (a: number[], b: number[], c: number[], d: number[]): boolean =>
    ((orient(c, d, a) > 0) !== (orient(c, d, b) > 0)) &&
    ((orient(a, b, c) > 0) !== (orient(a, b, d) > 0))

// remove repeated points (a double-click adds the last point twice)
export const cleanPoints = (pts: Pt[]): Pt[] =>
    pts.filter((p, i) => i === 0 || Math.abs(p[0] - pts[i - 1][0]) > 1e-9 || Math.abs(p[1] - pts[i - 1][1]) > 1e-9)

export const centreOf = (pts: Pt[]): Pt => {
    const n = pts.length || 1
    return [pts.reduce((a, p) => a + p[0], 0) / n, pts.reduce((a, p) => a + p[1], 0) / n]
}

/** true when the area geometry (Polygon / MultiPolygon) is inside or overlaps the drawn shape */
export const polygonHitsGeometry = (drawn: Pt[], geom: any): boolean => {
    if (drawn.length < 3 || !geom) return false

    const polys: number[][][][] =
        geom.type === 'Polygon' ? [geom.coordinates] :
        geom.type === 'MultiPolygon' ? geom.coordinates : []

    const ring = [...drawn, drawn[0]]
    const dMinX = Math.min(...drawn.map((p) => p[0]))
    const dMaxX = Math.max(...drawn.map((p) => p[0]))
    const dMinY = Math.min(...drawn.map((p) => p[1]))
    const dMaxY = Math.max(...drawn.map((p) => p[1]))

    for (const poly of polys) {
        const outer = poly[0]
        if (!outer?.length) continue

        // quick box check first
        let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity
        for (const [x, y] of outer) {
            if (x < minX) minX = x
            if (x > maxX) maxX = x
            if (y < minY) minY = y
            if (y > maxY) maxY = y
        }
        if (maxX < dMinX || minX > dMaxX || maxY < dMinY || minY > dMaxY) continue

        // 1. drawn shape is inside the area (small shape inside a big county)
        if (drawn.some((p) => inRing(p, outer))) return true

        // 2. area is inside the drawn shape
        const step = Math.max(1, Math.floor(outer.length / 300))
        for (let i = 0; i < outer.length; i += step) {
            if (inRing(outer[i], ring)) return true
        }

        // 3. edges cross (partly covered)
        for (let i = 0; i + step < outer.length; i += step) {
            for (let j = 0; j < drawn.length; j++) {
                if (segmentsCross(outer[i], outer[i + step], ring[j], ring[j + 1])) return true
            }
        }
    }
    return false
}
